import { ContactGroupType } from 'contacts/enums';
import {
  IContactGroup,
  IContactGroupAddress,
  IContactGroupAddressListRequestPayload,
  IUpdateContactGroupDefaultPricingPayload,
} from 'contacts/types';
import { mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse, IRequestPayload } from 'core/types';
import { getQueryStringToSnakeCase } from 'core/utils';

export const getManyAutocomplete = async (
  requestPayload: IRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IContactGroup>> => {
  const { data } = await partfinitiClient.get(
    `/contacts/contact-group/autocomplete?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IContactGroup>(data.contact_groups, data.total_count);
};

export const getContactGroups = async (requestPayload: IRequestPayload, signal?: AbortSignal) => {
  const { data } = await partfinitiClient.get(
    `/contacts/contact-group/list?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  const contactGroups = data?.data.map((contactGroup: any) => ({
    ...contactGroup,
    type: [
      ...(contactGroup['is_customer'] ? [ContactGroupType.CUSTOMER] : []),
      ...(contactGroup['is_vendor'] ? [ContactGroupType.VENDOR] : []),
    ],
  }));

  return mapRecordsFromServer<IContactGroup>(contactGroups, data?.recordsFiltered);
};

export const getContactGroupAddresses = async (
  requestPayload: IContactGroupAddressListRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IContactGroupAddress>> => {
  const { data } = await partfinitiClient.get(
    `/contacts/contact-group-address/list?${getQueryStringToSnakeCase(requestPayload, {
      removeNullValues: true,
    })}`,
    signal
  );

  return mapContactGroupAddressResponse(data, requestPayload);
};

export const getContactGroupAddressesAutocomplete = async (
  requestPayload: IContactGroupAddressListRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IContactGroupAddress>> => {
  const { data } = await partfinitiClient.get(
    `/contacts/contact-group-address/autocomplete?${getQueryStringToSnakeCase(requestPayload, {
      removeNullValues: true,
    })}`,
    signal
  );

  return mapContactGroupAddressResponse(data, requestPayload);
};

export const updateContactGroup = async (data: Record<string, unknown>) =>
  await partfinitiClient.postForm('/contacts/contact-group/update', data, {
    includeNullValues: true,
  });

export const updateContactGroupDefaultPricing = async (
  data: IUpdateContactGroupDefaultPricingPayload
) => await partfinitiClient.postForm('/corporate/discounts/save', data);

export const mergeContactGroups = async (data: Record<string, unknown>) =>
  await partfinitiClient.postForm('/contacts/contact-group/merge', data);

export const mergeContactGroupAddresses = async (data: Record<string, unknown>) => {
  const result = await partfinitiClient.postForm('/contacts/contact-group-address/merge', data);

  return {
    ...result,
    contactGroupAddress: {
      ...result.contactGroupAddress,
      contactGroupType: [
        ...(result.contactGroupAddress?.contactGroupIsCustomer ? [ContactGroupType.CUSTOMER] : []),
        ...(result.contactGroupAddress?.contactGroupIsVendor ? [ContactGroupType.VENDOR] : []),
      ],
    },
  };
};

export const upsertContactGroupAddress = async (data: Record<string, unknown>) =>
  await partfinitiClient.postForm('/contacts/contact-group-address/save', data);

export const upsertContactGroup = async (data: Record<string, unknown>) =>
  await partfinitiClient.postForm('/contacts/contact-group/save', data);

const mapContactGroupAddressResponse = (
  data: { data: Record<string, unknown>[]; recordsFiltered: number },
  requestPayload: IContactGroupAddressListRequestPayload
): GetManyResponse<IContactGroupAddress> => {
  let contactGroupAddresses = data?.data.map((address: Record<string, unknown>) => {
    const contactGroup = address.contact_group as Record<string, unknown>;

    return {
      ...address,
      contactGroup: {
        ...contactGroup,
        type: [
          ...(contactGroup['is_customer'] ? [ContactGroupType.CUSTOMER] : []),
          ...(contactGroup['is_vendor'] ? [ContactGroupType.VENDOR] : []),
        ],
      },
    };
  }) as IContactGroupAddress[];

  if (
    requestPayload?.viewBy?.contactGroupId &&
    requestPayload?.viewBy?.includeOtherContactGroups !== 0
  ) {
    const targetGroupId = requestPayload.viewBy.contactGroupId;

    contactGroupAddresses = contactGroupAddresses.map((address: IContactGroupAddress) => {
      const contactGroup = address.contactGroup;

      return {
        ...address,
        mainContactGroup: Number(contactGroup.id) === Number(targetGroupId),
        otherContactGroup: Number(contactGroup.id) !== Number(targetGroupId),
      };
    });
  }

  return mapRecordsFromServer<IContactGroupAddress>(contactGroupAddresses, data.recordsFiltered);
};
